import { useCallback, useEffect, useState } from 'react';
import { request } from '@/api/client';
import { INDUSTRY_ROLES, INTERESTS } from '@/api/betaAccess';

type Entry = { id: string; email: string; created_at: string; attribution: Record<string, string> };
type Status = 'pending' | 'invited' | 'registered' | 'rejected' | 'deferred';
type Invitation = { id: string; state: 'active' | 'expired' | 'revoked' | 'redeemed'; expires_at: string } | null;
type Application = {
  id: string; email: string; full_name: string; organization_name: string; industry_role: string; shows_per_month: string;
  primary_region: string; status: Status; source: string | null; created_at: string; submission_count: number;
  invite_email_status: 'sent' | 'failed' | null; segment: 'artist' | 'industry'; account_exists: boolean; invitation: Invitation;
  primary_interest?: string; website?: string | null; admin_notes?: string | null; invited_at?: string | null;
  invite_send_count?: number; registered_at?: string | null; reviewed_at?: string | null;
};
type InviteResult = { application: Application; invite_url: string | null; email_status: 'sent' | 'failed' | null };

const STATUSES: Status[] = ['pending', 'invited', 'registered', 'deferred', 'rejected'];
const ROLE_LABEL = Object.fromEntries(INDUSTRY_ROLES);
const INTEREST_LABEL = Object.fromEntries(INTERESTS);
const BASE = '/auth/beta/applications';
const when = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—');
const button = 'rounded border px-3 py-2 text-sm disabled:opacity-50';

function statusLabel(row: Application) {
  if (row.status === 'invited') {
    if (row.invitation?.state === 'expired') return 'Invited · expired';
    if (row.invitation?.state === 'revoked') return 'Invited · link revoked';
    if (row.invite_email_status === 'failed') return 'Invited · email failed';
  }
  return row.status[0].toUpperCase() + row.status.slice(1);
}

function ApplicationDetail({ id, onChanged, onClose }: { id: string; onChanged: () => void; onClose: () => void }) {
  const [row, setRow] = useState<Application | null>(null);
  const [notes, setNotes] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [link, setLink] = useState<{ url: string; emailStatus: string | null } | null>(null);
  const [copied, setCopied] = useState(false);

  const show = useCallback((next: Application) => { setRow(next); setNotes(next.admin_notes || ''); setEmail(next.email); }, []);
  useEffect(() => {
    let active = true; setRow(null); setLink(null); setMessage(''); setError('');
    request(`${BASE}/${id}`).then((r: Application) => { if (active) show(r); }).catch((e: Error) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [id, show]);

  async function act(action: string, init?: { method: string; body?: object }) {
    setBusy(true); setError(''); setMessage(''); setCopied(false);
    try {
      const result: Application | InviteResult = await request(action ? `${BASE}/${id}/${action}` : `${BASE}/${id}`, init ?? { method: 'POST' });
      if ('application' in result) {
        show(result.application);
        if (result.invite_url) {
          setLink({ url: result.invite_url, emailStatus: result.email_status });
          setMessage(result.email_status === 'sent' ? 'Invitation emailed.' : 'Email was not sent — copy the link below and send it yourself.');
        } else {
          setMessage('Already invited. Use Resend invitation to issue a new link.');
        }
      } else {
        show(result);
        setMessage(action ? `Application ${result.status}.` : 'Saved.');
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!link) return;
    try { await navigator.clipboard.writeText(link.url); setCopied(true); } catch { setCopied(false); }
  }

  if (!row) return <section className="rounded border p-4" aria-live="polite">{error ? <p role="alert">{error}</p> : <p role="status">Loading application…</p>}</section>;
  const canInvite = (row.status === 'pending' || row.status === 'deferred') && !row.account_exists;
  return (
    <section className="space-y-4 rounded border p-4" aria-labelledby="beta-app-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="beta-app-detail" className="text-xl font-semibold">{row.full_name}</h2>
          <p className="text-sm">{row.organization_name} · {statusLabel(row)}{row.account_exists ? ' · Account already exists' : ''}</p>
        </div>
        <button className={button} onClick={onClose}>Close</button>
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div><dt className="font-medium">Email</dt><dd className="break-all">{row.email}</dd></div>
        <div><dt className="font-medium">Relationship</dt><dd>{ROLE_LABEL[row.industry_role] ?? row.industry_role} ({row.segment})</dd></div>
        <div><dt className="font-medium">Shows per month</dt><dd>{row.shows_per_month}</dd></div>
        <div><dt className="font-medium">Region</dt><dd>{row.primary_region}</dd></div>
        <div><dt className="font-medium">Primary interest</dt><dd>{INTEREST_LABEL[row.primary_interest ?? ''] ?? row.primary_interest}</dd></div>
        <div><dt className="font-medium">Website</dt><dd className="break-all">{row.website ? <a className="underline" href={row.website} target="_blank" rel="noopener noreferrer">{row.website}</a> : '—'}</dd></div>
        <div><dt className="font-medium">Source</dt><dd>{row.source || 'Direct'}</dd></div>
        <div><dt className="font-medium">Received</dt><dd>{when(row.created_at)}{row.submission_count > 1 ? ` · submitted ${row.submission_count}×` : ''}</dd></div>
        <div><dt className="font-medium">Invitation</dt><dd>{row.invitation ? `${row.invitation.state}, expires ${when(row.invitation.expires_at)} · sent ${row.invite_send_count ?? 0}× · email ${row.invite_email_status ?? '—'}` : 'Not invited'}</dd></div>
        <div><dt className="font-medium">Registration</dt><dd>{row.status === 'registered' ? `Registered ${when(row.registered_at)}` : 'Not registered'}</dd></div>
      </dl>

      {link && (
        <div className="space-y-2 rounded border p-3">
          <label className="block text-sm font-medium" htmlFor="beta-invite-link">Invitation link (shown once — single use, only for {row.email})</label>
          <div className="flex flex-wrap gap-2">
            <input id="beta-invite-link" className="min-w-0 flex-1 rounded border px-2 py-1 font-mono text-xs" readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} />
            <button className={button} onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {(row.status === 'pending' || row.status === 'deferred') && <button className={`${button} font-semibold`} disabled={busy || !canInvite} onClick={() => act('invite')}>Approve &amp; invite</button>}
        {row.status === 'invited' && <button className={button} disabled={busy || row.account_exists} onClick={() => act('resend')}>Resend invitation</button>}
        {(row.status === 'pending' || row.status === 'invited') && <button className={button} disabled={busy} onClick={() => act('defer')}>Defer (future access)</button>}
        {(row.status === 'pending' || row.status === 'deferred' || row.status === 'invited') && <button className={button} disabled={busy} onClick={() => { if (window.confirm(`Reject ${row.full_name}? Any unused invitation link stops working.`)) void act('reject'); }}>Reject</button>}
        {(row.status === 'rejected' || row.status === 'deferred') && <button className={button} disabled={busy} onClick={() => act('reopen')}>Reopen</button>}
      </div>
      {message && <p role="status" className="text-sm">{message}</p>}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">Internal notes
          <textarea className="mt-1 block w-full rounded border p-2" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <label className="block text-sm">Email (changing it revokes any unused invitation)
          <input className="mt-1 block w-full rounded border p-2" type="email" value={email} disabled={row.status === 'registered' || row.status === 'rejected'} onChange={(e) => setEmail(e.target.value)} />
        </label>
      </div>
      <button className={button} disabled={busy} onClick={() => act('', { method: 'PATCH', body: { admin_notes: notes, ...(email.trim().toLowerCase() !== row.email ? { email: email.trim() } : {}) } })}>Save notes &amp; email</button>
    </section>
  );
}

function ApplicationsPanel() {
  const [status, setStatus] = useState<'' | Status>('pending');
  const [role, setRole] = useState('');
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState<{ items: Application[]; total: number; counts: Record<Status, number> } | null>(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let active = true; setError('');
    const params = new URLSearchParams({ limit: '50', offset: String(offset) });
    if (status) params.set('status', status);
    if (role) params.set('role', role);
    request(`${BASE}?${params}`)
      .then((result: typeof data) => { if (active) setData(result); })
      .catch(() => { if (active) setError('Unable to load applications. Platform owner access is required.'); });
    return () => { active = false; };
  }, [status, role, offset, refresh]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">Status
          <select className="ml-2 rounded border px-2 py-1" value={status} onChange={(e) => { setStatus(e.target.value as Status | ''); setOffset(0); }}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}{data ? ` (${data.counts[s]})` : ''}</option>)}
          </select>
        </label>
        <label className="text-sm">Relationship
          <select className="ml-2 rounded border px-2 py-1" value={role} onChange={(e) => { setRole(e.target.value); setOffset(0); }}>
            <option value="">All</option>
            {INDUSTRY_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <button className={button} onClick={() => setRefresh((v) => v + 1)}>Refresh</button>
      </div>
      {error && <p role="alert">{error}</p>}
      {selected && <ApplicationDetail id={selected} onChanged={() => setRefresh((v) => v + 1)} onClose={() => setSelected(null)} />}
      {data && <>
        <p className="text-sm">{data.total} application{data.total === 1 ? '' : 's'}</p>
        <div className="overflow-x-auto"><table className="w-full text-left text-sm">
          <thead><tr><th className="p-2">Applicant</th><th className="p-2">Artist / organization</th><th className="p-2">Relationship</th><th className="p-2">Shows/mo</th><th className="p-2">Region</th><th className="p-2">Received</th><th className="p-2">Status</th></tr></thead>
          <tbody>{data.items.map((row) => (
            <tr key={row.id} className={`border-t ${selected === row.id ? 'bg-black/5' : ''}`}>
              <td className="p-2"><button className="text-left underline" onClick={() => setSelected(row.id)}>{row.full_name}</button></td>
              <td className="p-2">{row.organization_name}</td>
              <td className="p-2">{ROLE_LABEL[row.industry_role] ?? row.industry_role}</td>
              <td className="p-2">{row.shows_per_month}</td>
              <td className="p-2">{row.primary_region}</td>
              <td className="p-2">{new Date(row.created_at).toLocaleDateString()}</td>
              <td className="p-2">{statusLabel(row)}{row.account_exists ? ' · has account' : ''}</td>
            </tr>
          ))}</tbody>
        </table></div>
        {data.total === 0 && <p>No applications{status ? ` with status ${status}` : ''}.</p>}
        <div className="flex gap-3">
          <button className={button} disabled={offset === 0} onClick={() => setOffset((v) => Math.max(0, v - 50))}>Previous</button>
          <button className={button} disabled={offset + 50 >= data.total} onClick={() => setOffset((v) => v + 50)}>Next</button>
        </div>
      </>}
    </div>
  );
}

function WaitlistPanel() {
  const [offset,setOffset]=useState(0);
  const [data,setData]=useState<{items:Entry[];total:number}|null>(null);
  const [error,setError]=useState('');
  const [refresh,setRefresh]=useState(0);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{
    let active=true;setLoading(true);setError('');setData(null);
    request(`/auth/beta/waitlist?limit=50&offset=${offset}`)
      .then((result:{items:Entry[];total:number})=>{if(active)setData(result);})
      .catch(()=>{if(active)setError('Unable to load signups. Platform owner access is required.');})
      .finally(()=>{if(active)setLoading(false);});
    return()=>{active=false;};
  },[offset,refresh]);
  return <div className="space-y-4">
    <p>Requests for access only. Joining the waitlist does not create an account or grant an invitation.</p>
    <button className="rounded border px-3 py-2" onClick={()=>setRefresh(v=>v+1)} disabled={loading}>Refresh</button>
    {loading&&<p role="status">Loading signups…</p>}
    {error&&<p role="alert">{error}</p>}
    {data&&<>
      <p>{data.total} signup{data.total===1?'':'s'}</p>
      <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr><th className="p-3">Email</th><th className="p-3">Received</th><th className="p-3">Attribution</th></tr></thead><tbody>
        {data.items.map(row=><tr key={row.id} className="border-t"><td className="p-3 break-all">{row.email}</td><td className="p-3">{new Date(row.created_at).toLocaleString()}</td><td className="p-3 break-all">{Object.entries(row.attribution).map(([key,value])=>`${key}: ${value}`).join(' · ')||'Direct'}</td></tr>)}
      </tbody></table></div>
      {data.total===0&&<p>No signups yet.</p>}
      <div className="flex gap-3"><button className="rounded border px-3 py-2" disabled={offset===0} onClick={()=>setOffset(v=>Math.max(0,v-50))}>Previous</button><button className="rounded border px-3 py-2" disabled={offset+50>=data.total} onClick={()=>setOffset(v=>v+50)}>Next</button></div>
    </>}
  </div>;
}

export default function BetaAdminPage() {
  const [tab, setTab] = useState<'applications' | 'waitlist'>('applications');
  const tabClass = (name: string) => `rounded-t border-b-2 px-3 py-2 ${tab === name ? 'border-current font-semibold' : 'border-transparent'}`;
  return <section className="mx-auto max-w-5xl space-y-4 p-6">
    <h1 className="text-2xl font-semibold">ArtistNav beta access</h1>
    <div role="tablist" aria-label="Beta access" className="flex gap-2 border-b">
      <button role="tab" aria-selected={tab === 'applications'} className={tabClass('applications')} onClick={() => setTab('applications')}>Applications</button>
      <button role="tab" aria-selected={tab === 'waitlist'} className={tabClass('waitlist')} onClick={() => setTab('waitlist')}>Waitlist</button>
    </div>
    <div role="tabpanel">{tab === 'applications' ? <ApplicationsPanel /> : <WaitlistPanel />}</div>
  </section>;
}
